import React, { useEffect, useState } from 'react';
import { Formik } from 'formik';
import { Flex, Gap } from 'lib/components/styles/layout';
import { ICombinedUserData, useAuth } from 'lib/context';
import { getTitle } from 'lib/utils/functions';
import styled from 'styled-components/macro';

import * as Yup from 'yup';

import { FILE_EXTENSION } from 'lib/hooks/useMediaRecorder';

import { FormikInputField } from 'lib/components/formik/FormikInputField';
import {
  DEFAULT_UPLOAD_ID,
  DEFAULT_UPLOAD_PROGRESS,
} from 'lib/api/aws/useS3UploadMutation';
import { UploadLoader } from '../../recordv1/UploadLoader';
import { DetailsPreviewRecordFormValues } from '../../recordv1/detailsPreview/types';
import { VideoPlayer } from '../../video/videoPlayer';
import { useVideoRecordUpload } from '../../recordv1/useVideoRecorderUpload';
import { Button } from 'react-covideo-common';
import { CardAlert } from 'lib/components/CardAlert';
import {
  MdOutlineRestartAlt,
  MdOutlineSentimentSatisfied,
} from 'react-icons/md';
import { useTheme } from 'styled-components/macro';

const DEFAULT_UPLOAD_FOLDER = {
  value: -2,
  label: 'Primary',
  isFolderShared: false,
  access: 'edit',
};

const Container = styled.div`
  padding-bottom: 64px;
  display: flex;
  position: relative;
  margin: 32px auto 0 auto;
  width: 1000px;
  flex-direction: column;
  @media screen and (max-width: 768px) {
    width: 100%;
  }
`;
const VideoWrapper = styled.div`
  max-width: 600px;
  .fullscreen {
    height: 274px;
    display: block;
  }
`;

const RecorderLoaderWrapper = styled(Gap)`
  width: 770px;
  height: 437px;
  margin-top: 90px;
`;

const generateName = (fileExt: string, userData: ICombinedUserData) => {
  const randomSuffix = (Math.random() + 1).toString(36).substring(2);
  const fileName = `${userData.userId}_${userData.customerId}_${randomSuffix}.${fileExt}`;
  return fileName;
};

export interface DetailsRecordingPreviewProps {
  videoBlob: Blob;
  setRerecordHandler?: () => void;
}

export const FreemiumPreview = ({
  videoBlob,
  setRerecordHandler,
}: DetailsRecordingPreviewProps) => {
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const { userData } = useAuth();
  const [cardVisible, setCardVisible] = useState(true);

  const { colors } = useTheme();
  const initialCovideoName = getTitle();
  const {
    setRecordingFormData,
    isLoading: isSubmittingForm,
    createProgress,
    encodeProgress,
    uploadToS3Progress,
    progressState,
  } = useVideoRecordUpload({ continueToSendShare: true, isFreemium: true });

  const uploadProgress =
    uploadToS3Progress[DEFAULT_UPLOAD_ID] || DEFAULT_UPLOAD_PROGRESS;

  useEffect(() => {
    const url = URL.createObjectURL(videoBlob);
    setVideoUrl(url);

    // Clean up the URL object when the component unmounts or the videoSource changes
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [videoBlob]);

  useEffect(() => {
    setCardVisible(true);
  }, []);

  const onSubmitHandler = (values: DetailsPreviewRecordFormValues) => {
    const fileName = generateName(FILE_EXTENSION, userData);
    const videoFile = new File([videoBlob], fileName, { type: videoBlob.type });
    setRecordingFormData({ ...values, file: videoFile });
  };

  const validationSchema = Yup.object().shape({
    title: Yup.string()
      .trim()
      .required('Title is required')
      .matches(/^[^.]*$/, 'Title should not contain a dot (.)'),
  });

  if (isSubmittingForm) {
    return (
      <RecorderLoaderWrapper>
        <UploadLoader
          progressState={progressState}
          encodeProgress={encodeProgress}
          uploadToS3Progress={uploadProgress}
          createProgress={createProgress}
        />
      </RecorderLoaderWrapper>
    );
  }

  const INITIAL_VALUES: DetailsPreviewRecordFormValues = {
    title: initialCovideoName,
    folder: DEFAULT_UPLOAD_FOLDER,
    videoTags: [],
  };

  return (
    <>
      {cardVisible && (
        <CardAlert
          handleClose={() => setCardVisible(false)}
          title='Wow, your video turned out great - way to go!'
          icon={
            <MdOutlineSentimentSatisfied
              size={22}
              color={colors.success[100]}
            />
          }
          backgroundColor={colors.success[5]}
          width={'460px'}
          top={'32px'}
          titleColor={colors.success[100]}
          content={
            <>
              Now you can give your video a title and save it to your library.
            </>
          }
        />
      )}
      <Container>
        <Formik
          initialValues={INITIAL_VALUES}
          validateOnMount={true}
          onSubmit={onSubmitHandler}
          validationSchema={validationSchema}
        >
          {({ submitForm, isValid }) => (
            <>
              <Flex
                flexDirection='column'
                width='100%'
                gap='12px'
                maxWidth='600px'
              >
                <Button
                  onClick={setRerecordHandler}
                  text='Rerecord'
                  icon={<MdOutlineRestartAlt size={20} />}
                  extendStyles={{
                    container: { width: 'fit-content', margin: '0 auto' },
                  }}
                  variant='destructive'
                />
                <VideoWrapper
                  style={{
                    height: '350px',
                    width: '100%',
                  }}
                >
                  {videoUrl !== null && (
                    <VideoPlayer
                      width='100%'
                      videoRef={React.createRef()}
                      videoSource={videoUrl}
                      disableControls
                      isFreemium
                      playerBackgroundColor={'rgba(0, 27, 83, 0.6)'}
                    />
                  )}
                </VideoWrapper>
                <Flex
                  flexDirection='row'
                  gap='24px'
                  justifyContent='center'
                  alignItems='center'
                  width='85%'
                  margin='0 auto'
                >
                  <FormikInputField
                    name='title'
                    label='Video Title'
                    isRequired
                    extendStyles={{
                      mainWrapper: {
                        flex: '.7 !important',
                      },
                    }}
                  />
                  <Button
                    disabled={!isValid}
                    text='Title Video & Save'
                    extendStyles={{ container: { flex: 0.3 } }}
                    onClick={submitForm}
                  />
                </Flex>
              </Flex>
            </>
          )}
        </Formik>
      </Container>
    </>
  );
};
